/**
 * The module registry.
 *
 * This application is the first module of a platform that will also carry repository
 * readiness and an agentic development kit. The extension point is built and proved now,
 * because an extension point that has never carried a second module is a guess.
 *
 * A module declares its own navigation, routes, and role requirements. Registering one
 * means creating its directory and adding one entry to `src/modules/index.ts`. Nothing
 * else changes -- and `prove-module-extension-point.test.tsx` asserts exactly that.
 */

import type { RouteObject } from 'react-router'

import type { FeatureKey, RuntimeConfig } from '../config/runtime'
import type { Role } from '../auth/identity'

export interface NavItem {
  readonly id: string
  readonly label: string
  readonly to: string
  readonly iconName: string
  readonly activeIconName?: string
  /** When present, only these roles see the item. */
  readonly requiredRoles?: readonly Role[]
  readonly featureFlag?: FeatureKey
}

export interface NavGroup {
  readonly id: string
  readonly label: string
  readonly iconName: string
}

export interface ModuleDefinition {
  readonly id: string
  readonly title: string
  readonly requiredRoles?: readonly Role[]
  readonly featureFlag?: FeatureKey
  readonly nav: {
    readonly group?: NavGroup
    readonly items: readonly NavItem[]
  }
  readonly routes: readonly RouteObject[]
  /**
   * Routes mounted outside the authenticated shell -- a sibling of the
   * `RequireAuth`-wrapped tree, not a child of it (see `router.tsx`). Optional, and
   * expected to stay rare: a module declares one only for a surface that must work for
   * a visitor with no session at all, such as a signed, single-use link an email
   * carries. Still role-filtered by nothing, same as `routes`, because there is no
   * session here to filter by.
   */
  readonly publicRoutes?: readonly RouteObject[]
}

export interface NavEntry {
  readonly item: NavItem
  readonly group?: NavGroup
}

function permitted(
  requiredRoles: readonly Role[] | undefined,
  roles: readonly Role[],
): boolean {
  // No declared requirement means every authenticated user, which is the right default
  // for a personal screen. An empty array is treated the same way rather than as "nobody",
  // because "nobody" is never what somebody means by writing an empty list.
  if (requiredRoles === undefined || requiredRoles.length === 0) return true
  return requiredRoles.some((role) => roles.includes(role))
}

function flagSet(
  featureFlag: FeatureKey | undefined,
  config: RuntimeConfig,
): boolean {
  return featureFlag === undefined || config.features[featureFlag]
}

export class ModuleRegistry {
  private readonly modules: readonly ModuleDefinition[]

  constructor(modules: readonly ModuleDefinition[]) {
    const seen = new Set<string>()
    for (const module of modules) {
      if (seen.has(module.id)) {
        throw new Error(`Duplicate module registration: ${module.id}`)
      }
      seen.add(module.id)
    }
    this.modules = modules
  }

  /** Modules whose feature flag, if any, is set. Role filtering happens per item. */
  available(config: RuntimeConfig): readonly ModuleDefinition[] {
    return this.modules.filter((module) => flagSet(module.featureFlag, config))
  }

  /**
   * Routes to mount.
   *
   * Role filtering is deliberately *not* applied here. A route the user cannot use must
   * still exist, so that opening its URL renders a no-access state rather than a
   * not-found -- the former tells them their access is wrong, the latter tells them the
   * page is wrong, and only one of those is true.
   */
  routes(config: RuntimeConfig): readonly RouteObject[] {
    return this.available(config).flatMap((module) => module.routes)
  }

  /** Routes a module declared as unauthenticated -- see `ModuleDefinition.publicRoutes`. */
  publicRoutes(config: RuntimeConfig): readonly RouteObject[] {
    return this.available(config).flatMap((module) => module.publicRoutes ?? [])
  }

  /** Navigation entries this user should see, in registration order. */
  navigation(
    config: RuntimeConfig,
    roles: readonly Role[],
  ): readonly NavEntry[] {
    const entries: NavEntry[] = []
    for (const module of this.available(config)) {
      if (!permitted(module.requiredRoles, roles)) continue
      for (const item of module.nav.items) {
        if (!flagSet(item.featureFlag, config)) continue
        if (!permitted(item.requiredRoles, roles)) continue
        entries.push(
          module.nav.group === undefined
            ? { item }
            : { item, group: module.nav.group },
        )
      }
    }
    return entries
  }

  /**
   * Where to send a user who has just signed in.
   *
   * The first navigation item their roles permit. A developer therefore lands on their own
   * usage and a manager on the overview, without either being hard-coded.
   */
  landingPath(
    config: RuntimeConfig,
    roles: readonly Role[],
  ): string | undefined {
    return this.navigation(config, roles)[0]?.item.to
  }

  get size(): number {
    return this.modules.length
  }

  ids(): readonly string[] {
    return this.modules.map((module) => module.id)
  }
}
